import { faucetDisplayUrl } from '@/core/faucet-post';

import {
  CARD_ART,
  CARD_PX,
  LINK_BACKDROP,
  STORY_BACKDROP,
  TRACK_DISPLAY,
  TRACK_LABEL,
  WORDMARK_SRC,
  estimateWidth,
  fit,
  plateOp,
  qrOp,
  text,
  type CardLayout,
  type CardOp,
  type CardSize,
  type Measure,
} from './layout';

/**
 * The faucet card: what a player posts on X to claim their weekly tokens. Blockari's `share/faucet-layout.ts`
 * (17aabed) in 21 Wild's art: the score cards' own key art and plate (the felt under the night sky, the wordmark on
 * its arc, a dark plate carrying every word — text never lands on raw art), a big "100" with the $CHAIN coin and
 * "FREE TOKENS", and a QR of the personal link with the link printed under it. Two sizes from one function: the
 * STORY (1080×1920, the share sheet) and the LINK (1200×630, what the `/f/:code` page unfurls into on X).
 *
 * Pure data, like `layout.ts`: no DOM, no canvas, every number named, testable in Node. The post's own words
 * (what the player posts) are in `core/faucet-post`.
 */

export interface FaucetCardInput {
  size: CardSize;
  /** The faucet amount on the headline (the server's FAUCET_AMOUNT). */
  amount: number;
  /** The personal link with its scheme: what the QR encodes. */
  url: string;
}

export const FAUCET_EYEBROW = 'WEEKLY FAUCET';
export const FAUCET_HEADLINE = 'FREE TOKENS';
export const FAUCET_SCAN = 'SCAN TO CLAIM YOURS';

const STORY = {
  // The plate is the score story card's own: from under the wordmark to the foot. Offsets below are from its top.
  plate: STORY_BACKDROP.plate,
  padX: 48,
  eyebrowDy: 74,
  eyebrowSize: 40,
  figureDy: 250,
  figureSize: 300,
  figureGlow: 70,
  coin: 236,
  coinGap: 40,
  headlineDy: 440,
  headlineSize: 124,
  scanDy: 540,
  scanSize: 38,
  qrDy: 590,
  qrTile: 372,
  qrPad: 24,
  qrR: 28,
  linkDy: 1066,
  linkSize: 40,
} as const;

const LINK = {
  plate: LINK_BACKDROP.plate,
  padX: LINK_BACKDROP.padX,
  leftX: 60,
  leftW: 840,
  eyebrowY: 366,
  eyebrowSize: 22,
  figureY: 448,
  figureSize: 140,
  figureGlow: 34,
  coin: 112,
  coinGap: 22,
  headlineY: 542,
  headlineSize: 64,
  // The link sits under the QR, right-aligned with it: X overlays its domain pill on the image's bottom-left
  // corner, so nothing is printed there.
  linkY: 578,
  linkSize: 17,
  linkMaxW: 400,
  qrTile: 168,
  qrPad: 14,
  qrR: 20,
  scanGap: 17,
  scanSize: 15,
} as const;

/** The coin and the figure as one centred row: the coin first, a gap, the figure. */
function figureRow(
  figure: string,
  size: number,
  maxW: number,
  coin: number,
  gap: number,
  cx: number,
  y: number,
  glow: number,
  measure: Measure,
): CardOp[] {
  const fs = fit(figure, size, maxW - coin - gap, measure, 'display', 700, TRACK_DISPLAY);
  const w = measure(figure, 'display', fs, 700, TRACK_DISPLAY);
  const total = coin + gap + w;
  const left = cx - total / 2;
  return [
    { kind: 'coin', x: left, y: y - coin / 2, size: coin },
    text('figure', figure, left + coin + gap, y, fs, { tone: 'mint', align: 'left', track: TRACK_DISPLAY, glow }),
  ];
}

export function layoutFaucetCard(input: FaucetCardInput, measure: Measure = estimateWidth): CardLayout {
  return input.size === 'story' ? layoutStory(input, measure) : layoutLink(input, measure);
}

function layoutStory(input: FaucetCardInput, measure: Measure): CardLayout {
  const { w, h } = CARD_PX.story;
  const M = STORY;
  const cx = w / 2;
  const top = M.plate.y;
  const contentW = M.plate.w - 2 * M.padX;
  const link = faucetDisplayUrl(input.url);
  const ops: CardOp[] = [
    { kind: 'art', src: CARD_ART.story, w, h, crop: STORY_BACKDROP.artCrop, wordmark: { src: WORDMARK_SRC, cx, ...STORY_BACKDROP.wordmark } },
    plateOp(M.plate),
    text('eyebrow', FAUCET_EYEBROW, cx, top + M.eyebrowDy, M.eyebrowSize, { tone: 'amber', weight: 600, track: TRACK_LABEL }),
    ...figureRow(String(input.amount), M.figureSize, contentW, M.coin, M.coinGap, cx, top + M.figureDy, M.figureGlow, measure),
    text('headline', FAUCET_HEADLINE, cx, top + M.headlineDy, fit(FAUCET_HEADLINE, M.headlineSize, contentW, measure, 'display', 700, TRACK_LABEL), {
      tone: 'text',
      track: TRACK_LABEL,
    }),
    text('scan', FAUCET_SCAN, cx, top + M.scanDy, M.scanSize, { tone: 'dim', weight: 600, track: TRACK_LABEL }),
    qrOp(input.url, cx - M.qrTile / 2, top + M.qrDy, M),
    text('link', link, cx, top + M.linkDy, fit(link, M.linkSize, contentW, measure, 'body', 500), { face: 'body', weight: 500, tone: 'mint' }),
  ];
  return { size: 'story', w, h, ops };
}

function layoutLink(input: FaucetCardInput, measure: Measure): CardLayout {
  const { w, h } = CARD_PX.link;
  const M = LINK;
  const leftCx = M.leftX + M.leftW / 2;
  const link = faucetDisplayUrl(input.url);
  const qrX = M.plate.x + M.plate.w - M.padX - M.qrTile;
  const qrY = M.plate.y + 20;
  const ops: CardOp[] = [
    { kind: 'art', src: CARD_ART.link, w, h, wordmark: { src: WORDMARK_SRC, cx: w / 2, ...LINK_BACKDROP.wordmark } },
    plateOp(M.plate),
    text('eyebrow', FAUCET_EYEBROW, leftCx, M.eyebrowY, M.eyebrowSize, { tone: 'amber', weight: 600, track: TRACK_LABEL }),
    ...figureRow(String(input.amount), M.figureSize, M.leftW, M.coin, M.coinGap, leftCx, M.figureY, M.figureGlow, measure),
    text('headline', FAUCET_HEADLINE, leftCx, M.headlineY, fit(FAUCET_HEADLINE, M.headlineSize, M.leftW, measure, 'display', 700, TRACK_LABEL), {
      tone: 'text',
      track: TRACK_LABEL,
    }),
    qrOp(input.url, qrX, qrY, M),
    text('scan', FAUCET_SCAN, qrX + M.qrTile / 2, qrY + M.qrTile + M.scanGap, M.scanSize, { tone: 'dim', weight: 600, track: TRACK_LABEL }),
    text('link', link, qrX + M.qrTile, M.linkY, fit(link, M.linkSize, M.linkMaxW, measure, 'body', 500), {
      face: 'body',
      weight: 500,
      tone: 'mint',
      align: 'right',
    }),
  ];
  return { size: 'link', w, h, ops };
}
